import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { ECharts, EChartsOption } from 'echarts';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { ChartColorService } from 'src/app/services/chart-color.service';
import { CommonLegendService } from 'src/app/services/common-legend.service';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { getGcpProjectIdForEnv } from 'src/app/services/data-environment/models/data-environment.model';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import { IQueryContext } from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsAxisHelper } from 'src/app/utils/echarts/echarts-axis-helper';
import { EChartsTooltipHelper } from 'src/app/utils/echarts/echarts-tooltip-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { DatasourceColumnOldDataLoaderFactoryOptions } from '../common/data-loaders/datasource-column-old';
import { SegmentDatasourceColumnDataLoaderFactoryOptions } from '../common/data-loaders/segments';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { createDefaultSegmentDatasourceColumn } from '../common/items/filter/segment-filter/segments';
import { ModifyTimeDashboardItemFactoryOptions } from '../common/items/modify-time';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { BiVizDashboardUsersActivity } from './sql-maps/biviz-dashboard-users-activity-datasource-sql-map';

@Component({
    selector: 'app-lastarchon-users',
    templateUrl: './lastarchon-users.component.html',
    styleUrls: ['./lastarchon-users.component.scss'],
    providers: [DashboardService, QueryContextService, CommonLegendService],
})
export class LastArchonUsersComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Users';
    override DASHBOARD_ID = 'lastarchon-users';
    values = Object.values;

    /** GCP Project id used in the dashboard */
    private gcpProjectId: GcpProjectId = 'am-lastarchon-dev';

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////

    override filterDataLoaders = {
        'lastarchon.users.filter.segment': DataLoaderFactories[
            'common.filter.segment'
        ]({
            createdInstanceId: 'lastarchon.users.filter_segments',
            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'SEGMENT',
        }).create(),
        'lastarchon.users.filter.activity_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.app_version',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ACTIVITY_APP_VERSION',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('ACTIVITY_APP_VERSION'),
        }).create(),
        'lastarchon.users.filter.activity_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.users.filter.activity_app_version_group_minor',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ACTIVITY_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                orderByQuery: datasourceColumnOrderByQuery(
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ),
            }).create(),
        'lastarchon.users.filter.first_geo_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.first_geo_country_tier',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            // filterContext: ['FIRST_GEO_COUNTRY'],
        }).create(),
        'lastarchon.users.filter.first_geo_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.first_geo_country',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: ['FIRST_GEO_COUNTRY_TIER'],
        }).create(),
        'lastarchon.users.filter.first_geo_country_eu': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.first_geo_country_eu',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_GEO_COUNTRY_EU',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
        }).create(),
        'lastarchon.users.filter.first_attribution_unified_traffic':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.users.filter.first_attribution_unified_traffic',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'lastarchon.users.filter.first_app_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.first_app_version',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIRST_APP_VERSION',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            filterContext: true,
            orderByQuery: datasourceColumnOrderByQuery('FIRST_APP_VERSION'),
        }).create(),
        'lastarchon.users.filter.first_app_version_group_minor':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.users.filter.first_app_version_group_minor',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_APP_VERSION_GROUP_MINOR',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
                orderByQuery: datasourceColumnOrderByQuery(
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            }).create(),
        'lastarchon.users.filter.etl_payer': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.etl_payer',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_PAYER',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
        }).create(),
        'lastarchon.users.filter.etl_revenue_badge': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'lastarchon.users.filter.etl_revenue_badge',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasourceColumnId: 'REVENUE_BADGE',
            datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery('REVENUE_BADGE'),
        }).create(),
        'lastarchon.users.filter.first_attribution_network_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.users.filter.first_attribution_network_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
            }).create(),
        'lastarchon.users.filter.first_attribution_campaign_name':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'lastarchon.users.filter.first_attribution_campaign_name',

                gcpProjectId: () => {
                    return getGcpProjectIdForEnv(this.dataEnv.current);
                },
                queryCacheService: this.queryCache,
                datasourceColumnId: 'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                datasourceSqlMap: BiVizDashboardUsersActivity.sqlMap,
                filterContext: true,
            }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    CHART DATALOADERS
    ////
    /////////////////////////////////////////////////////////////////

    override chartDataLoaders = {
        'lastarchon.users.modify_time_users_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId: 'lastarchon.users.modify_time_users_datasource',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
            datasetId: BiVizDashboardUsersActivity.sqlMap.dataset,
            tableId: BiVizDashboardUsersActivity.sqlMap.tableId,
        }).create(),

        'lastarchon.users.usersdash_activity_daily': DataLoaderFactories[
            'common.users.usersdash_activity_daily'
        ]({
            createdInstanceId: 'lastarchon.users.usersdash_activity_daily',
            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),

        'lastarchon.users.usersdash_activity_monthly': DataLoaderFactories[
            'common.users.usersdash_activity_monthly'
        ]({
            createdInstanceId: 'lastarchon.users.usersdash_activity_monthly',

            gcpProjectId: () => {
                return getGcpProjectIdForEnv(this.dataEnv.current);
            },
            queryCacheService: this.queryCache,
        }).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////

    // CHARTS
    modify_time_users_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.modify_time_users_datasource',
        dashboardItemFactory: DashboardItemFactories['common.modify_time'](
            new ModifyTimeDashboardItemFactoryOptions(
                'lastarchon.users.modify_time_users_datasource',
                'value',
            ),
        ),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'lastarchon.users.modify_time_users_datasource'
            ].emitter,
    };

    dau: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.chart.dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.users.chart.dau',
            dashboardItem: { title: 'DAU' },
            xColumnLabel: 'date',
            yColumnLabel: 'dau',
            metricName: 'DAU',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.users.usersdash_activity_daily']
                .emitter,
    };
    daily_new_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.chart.daily_new_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.users.chart.daily_new_users',
            dashboardItem: { title: 'Daily new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'line',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.users.usersdash_activity_daily']
                .emitter,
    };
    m_avg_dau: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.chart.m_avg_dau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.users.chart.m_avg_dau',
            dashboardItem: { title: 'Monthly average DAU' },
            xColumnLabel: 'date',
            yColumnLabel: 'avg_monthly_dau',
            metricName: 'Monthly avg DAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.users.usersdash_activity_monthly']
                .emitter,
    };
    mau: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.chart.mau',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.users.chart.mau',
            dashboardItem: { title: 'MAU' },
            xColumnLabel: 'date',
            yColumnLabel: 'mau',
            metricName: 'MAU',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.users.usersdash_activity_monthly']
                .emitter,
    };
    monthly_new_users: IDashboardItemDataLoaderLink = {
        id: 'lnk.lastarchon.users.chart.monthly_new_users',
        dashboardItemFactory: DashboardItemFactories['common.chart.xy_chart']({
            dashboardName: this.DASHBOARD_NAME,
            createdInstanceId: 'lastarchon.users.chart.monthly_new_users',
            dashboardItem: { title: 'Monthly new users' },
            xColumnLabel: 'date',
            yColumnLabel: 'new_users',
            metricName: 'New users',
            echartsSeriesType: 'bar',
            colorGetterBuilder: (context: IQueryContext) => {
                // get all split columns ids
                const splitColumnsIds = context.datasourceColumns
                    .filter((dc) => dc.state.isSplitApplied)
                    .map((dc) => dc.id);
                // create layer from spliColumns
                const layer = ChartColorService.getLayerString(splitColumnsIds);
                return (s: string) => ({
                    color: this.colorService.getColor(layer, s),
                });
            },
            echartsOptionsOverride: (
                context: IQueryContext,
                echartsOption: EChartsOption,
                echartInstance: ECharts,
            ) => {
                echartsOption.yAxis = {
                    axisLabel: {
                        formatter: EChartsAxisHelper.axisNumberLabelFormatter,
                    },
                };
                echartsOption.tooltip =
                    EChartsTooltipHelper.createDefaultTooltip(echartInstance, {
                        xLabelFormatter:
                            EChartsAxisHelper.axisLabelDateFormatter,
                        valueFormatter:
                            EChartsTooltipHelper.tooltipIntegerValueFormatter,
                    });
            },
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['lastarchon.users.usersdash_activity_monthly']
                .emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        ACTIVITY_DATE: {
            id: 'lastarchon.users.filter.activity_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'lastarchon.users.filter.activity_date',
                datasourceColumnId: 'ACTIVITY_DATE',
                splitable: false,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        ACTIVITY_APP_VERSION_GROUP_MINOR: {
            id: 'lastarchon.users.filter.app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.app_version_group_minor',
                    'ACTIVITY_APP_VERSION_GROUP_MINOR',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.activity_app_version_group_minor'
                ].emitter,
        },
        ACTIVITY_APP_VERSION: {
            id: 'lastarchon.users.filter.app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.app_version',
                    'ACTIVITY_APP_VERSION',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.activity_app_version'
                ].emitter,
        },
        SEGMENT: {
            id: 'lastarchon.users.filter.segments',
            dashboardItemFactory: DashboardItemFactories['filter.segments']({
                createdInstanceId: 'lastarchon.users.filter.segments',
            }),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.users.filter.segment']
                    .emitter,
        },
        FIRST_SESSION_DATE: {
            id: 'lastarchon.users.filter.FIRST_SESSION_DATE',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId: 'lastarchon.users.filter.FIRST_SESSION_DATE',
                datasourceColumnId: 'FIRST_SESSION_DATE',
                splitable: true,
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIRST_GEO_COUNTRY_EU: {
            id: 'lastarchon.users.filter.first_geo_country_eu',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_geo_country_eu',
                    'FIRST_GEO_COUNTRY_EU',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_geo_country_eu'
                ].emitter,
        },
        FIRST_GEO_COUNTRY_TIER: {
            id: 'lastarchon.users.filter.first_geo_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_geo_country_tier',
                    'FIRST_GEO_COUNTRY_TIER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_geo_country_tier'
                ].emitter,
        },
        FIRST_GEO_COUNTRY: {
            id: 'lastarchon.users.filter.first_geo_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_geo_country',
                    'FIRST_GEO_COUNTRY',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_geo_country'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_TRAFFIC: {
            id: 'lastarchon.users.filter.first_attribution_unified_traffic',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_attribution_unified_traffic',
                    'FIRST_ATTRIBUTION_UNIFIED_TRAFFIC',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_attribution_unified_traffic'
                ].emitter,
        },
        FIRST_APP_VERSION_GROUP_MINOR: {
            id: 'lastarchon.users.filter.first_app_version_group_minor',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_app_version_group_minor',
                    'FIRST_APP_VERSION_GROUP_MINOR',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_app_version_group_minor'
                ].emitter,
        },
        FIRST_APP_VERSION: {
            id: 'lastarchon.users.filter.first_app_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_app_version',
                    'FIRST_APP_VERSION',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_app_version'
                ].emitter,
        },
        REVENUE_PAYER: {
            id: 'lastarchon.users.filter.etl_payer',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.etl_payer',
                    'REVENUE_PAYER',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['lastarchon.users.filter.etl_payer']
                    .emitter,
        },
        REVENUE_BADGE: {
            id: 'lastarchon.users.filter.etl_revenue_badge',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.etl_revenue_badge',
                    'REVENUE_BADGE',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.etl_revenue_badge'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_NETWORK: {
            id: 'lastarchon.users.filter.first_attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_attribution_network_name',
                    'FIRST_ATTRIBUTION_UNIFIED_NETWORK',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_attribution_network_name'
                ].emitter,
        },
        FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN: {
            id: 'lastarchon.users.filter.first_attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'lastarchon.users.filter.first_attribution_campaign_name',
                    'FIRST_ATTRIBUTION_UNIFIED_CAMPAIGN',
                ),
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'lastarchon.users.filter.first_attribution_campaign_name'
                ].emitter,
        },
    };

    override async ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'LastArchonUsersComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }

    async ngAfterViewInit() {
        console.log({
            msg: 'LastArchonUsersComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const firebaseCountryTierIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIRST_GEO_COUNTRY_TIER',
                state: {
                    value: {
                        options: ['Tier 1', 'Tier 2', 'Tier 3'],
                        filterType: 'include',
                    },
                    // filterType: 'include',
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const activityDateIC: IDateRangePickerDashboardItem['datasourceColumn'] =
            {
                id: 'ACTIVITY_DATE',
                state: {
                    value: {
                        preset: 'last 365 days',
                        splitAggregate: 'day',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };
        const segmentIC = createDefaultSegmentDatasourceColumn();

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [
            // firebaseAppStoreIC,
            // firebaseCountryTierIC,
            activityDateIC,
            segmentIC,
        ];

        return initialContext;
    }
}
